import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import CustomSelectBox from "../common/CustomSelectBox";
import AdminDialog from "./AdminDialog";
import SupplierSpecialtiesDetail from "./SupplierSpecialtiesDetail";
import {
  getSupplierServices,
  createSupplierService,
  deleteSupplierService
} from "../../redux/supplierServices/thunks";
import {
  selectSupplierServicesBySupplier,
  selectSupplierServicesLoading
} from "../../redux/supplierServices/selectors";
import {
  getSupplierPricings
} from "../../redux/supplierPricing/thunks";
import {
  selectSupplierPricingsBySupplier
} from "../../redux/supplierPricing/selectors";
import { getSupplierServiceTypes } from "../../redux/supplierServiceTypes/thunks";
import { selectSupplierServiceTypes } from "../../redux/supplierServiceTypes/selectors";
import { openModal } from "../../redux/modal/modalSlice";
import AddIcon from "@material-ui/icons/Add";
import deleteIcon from "../../images/delete.svg";
import arrowDown from "../../images/arrow-down.svg";

const useStyles = makeStyles({
  wrapper: {
    padding: "1rem 1.25rem 1.25rem 1.25rem",
    backgroundColor: "#f8fafc",
    borderTop: "1px solid #cbd5e1",
    borderBottom: "1px solid #cbd5e1"
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    marginBottom: "1rem",
    flexWrap: "wrap"
  },
  title: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#1e293b"
  },
  servicesList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  },
  accordion: {
    border: "1px solid #e2e8f0",
    borderRadius: "0 !important",
    boxShadow: "none",
    "&:before": {
      display: "none"
    }
  },
  accordionSummary: {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    minHeight: "44px !important",
    "&.Mui-expanded": {
      minHeight: "44px !important",
      backgroundColor: "#f1f5f9"
    }
  },
  summaryContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingRight: "1rem"
  },
  summaryActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem"
  },
  serviceTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#334155"
  },
  chip: {
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontWeight: 500,
    fontSize: "0.75rem"
  },
  deleteButton: {
    padding: "4px",
    marginLeft: "4px"
  },
  accordionDetails: {
    padding: "0.5rem 0.75rem 0.75rem 0.75rem",
    backgroundColor: "#ffffff"
  },
  emptyText: {
    color: "#64748b",
    fontSize: "0.9rem",
    padding: "1rem 0"
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "1.5rem"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0.85rem",
    padding: "0.5rem 0"
  },
  fullWidth: {
    width: "100%"
  },
  deleteConfirmText: {
    fontSize: "0.95rem",
    padding: "1rem 0"
  }
});

function SupplierServicesDetail(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const supplier = props.data?.data || props.data || {};
  const supplierId = supplier.supplierId;
  const supplierDescription = supplier.description || "";

  const servicesFromStore = useSelector((state) =>
    selectSupplierServicesBySupplier(state, supplierId)
  );
  const servicesLoading = useSelector(selectSupplierServicesLoading);
  const pricings = useSelector((state) =>
    selectSupplierPricingsBySupplier(state, supplierId)
  );
  const serviceTypes = useSelector(selectSupplierServiceTypes);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState("");
  const [serviceToDelete, setServiceToDelete] = useState(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierServices({ param: supplierId }));
      dispatch(getSupplierPricings({ param: supplierId }));
    }
    if (!serviceTypes || serviceTypes.length === 0) {
      dispatch(getSupplierServiceTypes());
    }
  }, [dispatch, supplierId, serviceTypes]);

  const supplierServices = useMemo(() => {
    if (servicesFromStore && servicesFromStore.length > 0) {
      return servicesFromStore.map((s) => {
        const matchingPricings = (pricings || []).filter(
          (p) => Number(p.supplierServiceTypeId) === Number(s.supplierServiceTypeId)
        );
        const specialties = matchingPricings.length > 0 ? matchingPricings : (s.specialties || []);
        return {
          ...s,
          specialties,
          specialtiesCount: specialties.length
        };
      });
    }

    const serviceMap = new Map();
    (pricings || []).forEach((p) => {
      const typeId = p.supplierServiceTypeId;
      if (!serviceMap.has(typeId)) {
        serviceMap.set(typeId, {
          supplierId: supplierId,
          supplierServiceTypeId: typeId,
          serviceTypeName: p.serviceTypeName || "",
          specialties: [p],
          specialtiesCount: 1
        });
      } else {
        const item = serviceMap.get(typeId);
        item.specialties.push(p);
        item.specialtiesCount += 1;
      }
    });
    return Array.from(serviceMap.values());
  }, [servicesFromStore, pricings, supplierId]);

  const availableServiceTypesForLinking = useMemo(() => {
    const linkedIds = new Set(supplierServices.map((s) => Number(s.supplierServiceTypeId)));
    return (serviceTypes || []).filter(
      (st) => !linkedIds.has(Number(st.supplierServiceTypeId))
    );
  }, [supplierServices, serviceTypes]);

  const handleOpenCreateService = () => {
    setSelectedServiceTypeId("");
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setSelectedServiceTypeId("");
  };

  const handleSaveService = () => {
    if (!selectedServiceTypeId) {
      dispatch(openModal({ type: "warning", message: t("PleaseFillAllFields") }));
      return;
    }

    const payload = {
      supplierId: supplierId,
      supplierServiceTypeId: Number(selectedServiceTypeId)
    };

    dispatch(createSupplierService({ body: payload })).then((res) => {
      if (res.error) {
        dispatch(
          openModal({
            type: "warning",
            message: res.error.message || t("ServiceAlreadyLinked")
          })
        );
      } else {
        dispatch(getSupplierServices({ param: supplierId }));
        handleCloseCreateDialog();
      }
    });
  };

  const handleOpenDeleteService = (service, event) => {
    if (event) {
      event.stopPropagation();
    }
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleConfirmDeleteService = () => {
    if (!serviceToDelete) {
      return;
    }

    if (serviceToDelete.supplierServiceId) {
      dispatch(deleteSupplierService({ param: serviceToDelete.supplierServiceId })).then(() => {
        dispatch(getSupplierServices({ param: supplierId }));
        dispatch(getSupplierPricings({ param: supplierId }));
        handleCloseDeleteDialog();
      });
    } else {
      handleCloseDeleteDialog();
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {t("AvailableServices")} - {supplierDescription}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateService}
        >
          {t("LinkNewService")}
        </Button>
      </div>

      {servicesLoading === "loading" && supplierServices.length === 0 ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </div>
      ) : supplierServices.length === 0 ? (
        <Typography className={classes.emptyText}>
          {t("NoServicesFoundForSupplier")}
        </Typography>
      ) : (
        <div className={classes.servicesList}>
          {supplierServices.map((service) => (
            <Accordion
              key={service.supplierServiceTypeId}
              className={classes.accordion}
            >
              <AccordionSummary
                expandIcon={
                  <img
                    style={{ width: "0.75rem", height: "0.75rem" }}
                    src={arrowDown}
                    alt=""
                  />
                }
                className={classes.accordionSummary}
              >
                <div className={classes.summaryContent}>
                  <Typography className={classes.serviceTitle}>
                    {service.serviceTypeName}
                  </Typography>
                  <div className={classes.summaryActions}>
                    <Chip
                      size="small"
                      label={`${service.specialtiesCount} ${t("Specialties")}`}
                      className={classes.chip}
                    />
                    {service.supplierServiceId ? (
                      <IconButton
                        size="small"
                        className={classes.deleteButton}
                        onClick={(e) => handleOpenDeleteService(service, e)}
                        title={t("UnlinkService")}
                      >
                        <img height={16} src={deleteIcon} alt={t("UnlinkService")} />
                      </IconButton>
                    ) : null}
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <SupplierSpecialtiesDetail
                  supplierId={supplierId}
                  service={service}
                  specialties={service.specialties || []}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}

      <AdminDialog
        title={t("LinkNewService")}
        operation="create"
        dialogSize={{ width: "450px", height: "auto" }}
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        handleOnClick={handleSaveService}
      >
        <div className={classes.formGrid}>
          <div className={classes.fullWidth}>
            <CustomSelectBox
              label={t("SupplierServiceType")}
              dataSource={
                availableServiceTypesForLinking.length > 0
                  ? availableServiceTypesForLinking
                  : serviceTypes
              }
              displayExpr="description"
              valueExpr="supplierServiceTypeId"
              value={selectedServiceTypeId}
              onValueChanged={(e) => setSelectedServiceTypeId(e.value)}
              searchEnabled={true}
            />
          </div>
        </div>
      </AdminDialog>

      <AdminDialog
        title={t("UnlinkService")}
        operation="delete"
        dialogSize={{ width: "500px", height: "auto" }}
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        handleOnClick={handleConfirmDeleteService}
      >
        <Typography className={classes.deleteConfirmText}>
          {t("UnlinkServiceConfirmation")} {serviceToDelete ? `"${serviceToDelete.serviceTypeName}"` : ""}
        </Typography>
      </AdminDialog>
    </div>
  );
}

export default SupplierServicesDetail;
